import type { ReactNode } from "react";

export interface SgColumn {
  header: string;
  key: string;
  align?: "left" | "center" | "right";
  width?: string;
  render?: (row: any) => ReactNode;
}

interface SgTableProps {
  columns: SgColumn[];
  rows: any[];
  isDarkMode: boolean;
  loading?: boolean;
  emptyText?: string;
  rowKey?: (row: any) => string;
  onRowClick?: (row: any) => void;
}

const alignCls = (a?: string) =>
  a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";

export default function SgTable({
  columns,
  rows,
  isDarkMode,
  loading,
  emptyText = "Sin resultados",
  rowKey,
  onRowClick,
}: SgTableProps) {
  return (
    <div
      className={`rounded-xl border overflow-hidden shadow-sm ${
        isDarkMode
          ? "border-dark-border bg-dark-surface"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr
              className={
                isDarkMode
                  ? "bg-dark-card text-gray-300"
                  : "bg-gray-50 text-gray-500"
              }
            >
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ width: c.width }}
                  className={`px-4 py-3 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap ${alignCls(
                    c.align
                  )}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-gray-400"
                >
                  Cargando…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-gray-400"
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={rowKey ? rowKey(row) : i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-t transition-colors ${
                    isDarkMode
                      ? "border-dark-border hover:bg-white/5"
                      : "border-gray-100 hover:bg-gray-50"
                  } ${onRowClick ? "cursor-pointer" : ""}`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{ width: c.width }}
                      className={`px-4 py-3 align-middle ${alignCls(c.align)} ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
